import React from 'react';

import { useTranslation } from 'react-i18next';

import { FormLayout } from '@/components/layout';
import { LabelInput, TimePicker } from '@/components/ui/inputs';
import type { ScheduleWorkdayFormSubset } from '@/schemas/schedule/scheduleForm.schema';


interface ScheduleWorkdayFormProps {
  form: ScheduleWorkdayFormSubset;
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const ScheduleWorkdayForm: React.FC<ScheduleWorkdayFormProps> = ({ form }) => {
  const { t: translate } = useTranslation('schedule');
  const { register, watch, setValue, errors } = form;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="schedule-workday-form">

      {/* ── Inicio de jornada ───────────────────────────────────────── */}
      <FormLayout.Section title={translate('form.sections.workdayStart')}>
        <TimePicker
          label={translate('form.fields.startTime')}
          required
          showSeconds={false}
          value={watch('startTime')}
          onChange={(value) => setValue('startTime', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.startTime ? 'error' : undefined}
          supportingText={
            errors.startTime?.message ? translate(errors.startTime.message) : undefined
          }
          data-testid="schedule-start-time-picker"
        />

        <TimePicker
          label={translate('form.fields.entryThresholdStart')}
          required
          showSeconds={false}
          value={watch('entryThresholdStart')}
          onChange={(value) => setValue('entryThresholdStart', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.entryThresholdStart ? 'error' : undefined}
          supportingText={
            errors.entryThresholdStart?.message
              ? translate(errors.entryThresholdStart.message)
              : undefined
          }
          data-testid="schedule-entry-threshold-start-picker"
        />

        <TimePicker
          label={translate('form.fields.entryThresholdEnd')}
          required
          showSeconds={false}
          value={watch('entryThresholdEnd')}
          onChange={(value) => setValue('entryThresholdEnd', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.entryThresholdEnd ? 'error' : undefined}
          supportingText={
            errors.entryThresholdEnd?.message
              ? translate(errors.entryThresholdEnd.message)
              : undefined
          }
          data-testid="schedule-entry-threshold-end-picker"
        />

        <LabelInput
          label={translate('form.fields.entryOvertimeTolerance')}
          required
          type="number"
          registration={register('entryOvertimeTolerance')}
          color={errors.entryOvertimeTolerance ? 'error' : undefined}
          supportingText={
            errors.entryOvertimeTolerance?.message
              ? translate(errors.entryOvertimeTolerance.message)
              : undefined
          }
          data-testid="schedule-entry-overtime-tolerance-input"
        />

        <LabelInput
          label={translate('form.fields.entryDelayTolerance')}
          required
          type="number"
          registration={register('entryDelayTolerance')}
          color={errors.entryDelayTolerance ? 'error' : undefined}
          supportingText={
            errors.entryDelayTolerance?.message
              ? translate(errors.entryDelayTolerance.message)
              : undefined
          }
          data-testid="schedule-entry-delay-tolerance-input"
        />
      </FormLayout.Section>

      {/* ── Fin de jornada ──────────────────────────────────────────── */}
      <FormLayout.Section title={translate('form.sections.workdayEnd')}>
        <TimePicker
          label={translate('form.fields.endTime')}
          required
          showSeconds={false}
          value={watch('endTime')}
          onChange={(value) => setValue('endTime', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.endTime ? 'error' : undefined}
          supportingText={errors.endTime?.message ? translate(errors.endTime.message) : undefined}
          data-testid="schedule-end-time-picker"
        />

        <TimePicker
          label={translate('form.fields.exitThresholdStart')}
          required
          showSeconds={false}
          value={watch('exitThresholdStart')}
          onChange={(value) => setValue('exitThresholdStart', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.exitThresholdStart ? 'error' : undefined}
          supportingText={
            errors.exitThresholdStart?.message
              ? translate(errors.exitThresholdStart.message)
              : undefined
          }
          data-testid="schedule-exit-threshold-start-picker"
        />

        <TimePicker
          label={translate('form.fields.exitThresholdEnd')}
          required
          showSeconds={false}
          value={watch('exitThresholdEnd')}
          onChange={(value) => setValue('exitThresholdEnd', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.exitThresholdEnd ? 'error' : undefined}
          supportingText={
            errors.exitThresholdEnd?.message
              ? translate(errors.exitThresholdEnd.message)
              : undefined
          }
          data-testid="schedule-exit-threshold-end-picker"
        />

        <LabelInput
          label={translate('form.fields.exitOvertimeTolerance')}
          required
          type="number"
          registration={register('exitOvertimeTolerance')}
          color={errors.exitOvertimeTolerance ? 'error' : undefined}
          supportingText={
            errors.exitOvertimeTolerance?.message
              ? translate(errors.exitOvertimeTolerance.message)
              : undefined
          }
          data-testid="schedule-exit-overtime-tolerance-input"
        />

        <LabelInput
          label={translate('form.fields.earlyExitTolerance')}
          required
          type="number"
          registration={register('earlyExitTolerance')}
          color={errors.earlyExitTolerance ? 'error' : undefined}
          supportingText={
            errors.earlyExitTolerance?.message
              ? translate(errors.earlyExitTolerance.message)
              : undefined
          }
          data-testid="schedule-early-exit-tolerance-input"
        />
      </FormLayout.Section>

    </FormLayout>
  );
};

export default ScheduleWorkdayForm;
